/** One report per filing owed, read for the home screen's readiness card.
 *
 * **The same query key as `useReport`**, so a report the Reports screen already holds is not read
 * twice and opening a filing from here is instant. `useQueries` rather than one hook per row
 * because the ring needs every filing's state at once, and a hook called in a loop is not one.
 *
 * A report is a *preview of what the filing would state*, and its verdict is the product's own
 * word for where the checks stand (`Verdict`). This reads it and nothing else: no figure on the
 * card comes from here, only a position on a ladder and a count of blocking findings.
 */

import { useQueries } from "@tanstack/react-query";

import { api, keys } from "../api/client";
import type { Obligation, Report, Scope2Method } from "../api/queries";
import { unwrap } from "../api/queries";

export function useFilingReports(obligations: readonly Obligation[], method: Scope2Method) {
  return useQueries({
    queries: obligations.map((o) => ({
      queryKey: keys.report(o.obligation_id, method),
      queryFn: async (): Promise<Report> =>
        unwrap(
          await api.GET("/api/v1/reports/obligations/{obligation_id}", {
            params: { path: { obligation_id: o.obligation_id }, query: { scope2_method: method } },
          }),
        ),
    })),
  });
}

/** How many of a report's findings stop it, as the server graded them. */
export function blockingCount(report: Pick<Report, "findings">): number {
  return report.findings.filter((f) => f.severity === "blocking").length;
}
